import { useEffect, useState } from 'react';
import { Image } from '@mantine/core';
import {
  cdnLayoutImages,
  cdnLayoutImagesThumb,
  cdnPreviewImages,
  cdnPreviewImagesThumb,
} from '@/ApiPaths';
import {
  buildProductPreviewUrl,
  type PreviewImageAccess,
} from '@/utils/previewImageUrl';

type ProductListPreviewPictureProps = {
  /** Product or layout document id. */
  entityId: string;
  isLayout?: boolean;
  /** Stored filename under modelImg (often `{id}.png` or legacy `uuid.png`). */
  productImage?: string | null;
  /** Product only: from API; busts CDN/browser cache when preview PNG changes */
  imageVersion?: number | null;
  /** Signed access for password/restricted products (from product API). */
  previewImageAccess?: PreviewImageAccess | null;
  /** Temporary share token from `?share=` — also accepted by the CDN gate. */
  shareToken?: string | null;
  alt: string;
  fit?: 'contain' | 'cover';
};

/** WebP thumb basename must match the PNG basename on the server (e.g. uuid.webp for uuid.png). */
function previewThumbWebpKey(
  isLayout: boolean,
  entityId: string,
  productImage?: string | null
): string | null {
  if (isLayout) return `${entityId}.webp`;
  if (productImage?.toLowerCase().endsWith('.png')) {
    return `${productImage.slice(0, -'.png'.length)}.webp`;
  }
  return null;
}

/**
 * Loads a small WebP thumbnail when present; on 404 or missing thumb falls back to full PNG/JPEG URL once.
 */
export function ProductListPreviewPicture({
  entityId,
  isLayout = false,
  productImage,
  imageVersion,
  previewImageAccess,
  shareToken,
  alt,
  fit = 'contain',
}: ProductListPreviewPictureProps) {
  const pngSrc = isLayout
    ? `${cdnLayoutImages}${entityId}.png`
    : productImage
      ? buildProductPreviewUrl(`${cdnPreviewImages}${productImage}`, {
          imageVersion,
          access: previewImageAccess,
          share: shareToken,
        })
      : null;

  const webpKey = previewThumbWebpKey(isLayout, entityId, productImage);
  const webpSrc = webpKey
    ? isLayout
      ? `${cdnLayoutImagesThumb}${webpKey}`
      : buildProductPreviewUrl(`${cdnPreviewImagesThumb}${webpKey}`, {
          imageVersion,
          access: previewImageAccess,
          share: shareToken,
        })
    : null;

  const [useFallbackPng, setUseFallbackPng] = useState(false);

  useEffect(() => {
    setUseFallbackPng(false);
  }, [entityId, productImage, imageVersion, isLayout, pngSrc, webpSrc]);

  if (!pngSrc) return null;

  const tryWebpFirst = Boolean(webpSrc);
  const displaySrc =
    tryWebpFirst && !useFallbackPng ? (webpSrc as string) : pngSrc;

  return (
    <Image
      src={displaySrc}
      alt={alt}
      fit={fit}
      loading="lazy"
      decoding="async"
      style={{
        // Mantine Image sets `flex: 0`; in our flex wrappers Firefox then sizes the img to 0px (invisible).
        flex: 'none',
        width: '100%',
        height: '100%',
        objectPosition: 'center',
      }}
      onError={() => {
        if (tryWebpFirst && !useFallbackPng && displaySrc === webpSrc) {
          setUseFallbackPng(true);
        }
      }}
    />
  );
}
